/**
 * SchoolsEditorialChapter — one editorial chapter shared across the
 * three schools/prom/leavers landing pages.
 *
 * Per Craig's brand directive: the schools landing surfaces are
 * Mode 1 atmospheric pages. They must read as preserved-night
 * storytelling, NEVER as conversion pages. Narrative architecture —
 * not feature-grid architecture:
 *
 *     1.  The night arrives ✦       (hero)
 *     2.  The room reacts together ✦ (atmospheric vignette)
 *     3.  The memories remain ✦      (artifact preview)
 *     4.  The archive waits ✦        (shelf vignette)
 *     5.  Closing                    (restrained CTA)
 *
 * Reference points: yearbook opening at night, a farewell exhibition,
 * a preserved final chapter — NOT app-store landing, NOT pricing
 * comparison. Carry forward walnut / parchment / warm ivory / serif
 * headlines / handwritten annotations / restrained gold / breathing
 * atmosphere / asymmetry / quiet motion.
 *
 * Audience routing:
 *   • year6  — primary-school leavers, tender bittersweet tone
 *   • prom   — Year 11/13 prom, celebration tone
 *   • hub    — /schools umbrella, archival catalogue tone
 */
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';

const SERIF = 'Georgia, "Cormorant Garamond", "Times New Roman", serif';
const IVORY = '#FBEED0';
const GOLD = '#D4AF5E';
const GOLD_MUTED = 'rgba(212,170,82,0.78)';
const INK_DIM = 'rgba(255,235,200,0.62)';
const PAPER_BG = '#0E0805';

// ── audience copy ──────────────────────────────────────────────────
export const AUDIENCE = {
  year6: {
    label: 'Year 6 Leavers',
    nav_back: 'EventDrop ✦',
    hero_eyebrow: 'For Year 6 leavers ✦',
    hero_title: 'A year group, writing itself ✦',
    hero_sub:
      'Photos, notes, small confessions \u2014 the year group adding to the same room while the term is still happening. Proof of who this class became.',
    hero_primary: 'Open the leavers room ✦',
    react_title: 'The room reacts together ✦',
    react_body:
      'When the year group scans the same QR, the room starts forming. Missions arrive on the projector. The class doesn\u2019t just finish \u2014 it becomes something.',
    memories_eyebrow: 'CHAPTER TWO ✦',
    memories_title: 'What this class became ✦',
    memories_sub:
      'A yearbook the year group writes together, as the term is still happening.',
    memories_cards: [
      { pen: '#C84A4A', caption: 'My favourite year ♡' },
      { pen: '#D4AF5E', caption: 'Mrs Owen for everything ✦' },
      { pen: '#3A6CB3', caption: 'See you in big school' },
    ],
    archive_eyebrow: 'CHAPTER THREE ✦',
    archive_title: 'A chapter the school can still point to ✦',
    archive_sub:
      'Sports Day. Summer Fair. Leavers Night. Each chapter \u2014 the year group, written down by the year group, returned to whenever the school wants.',
    archive_spines: [
      { name: 'Sports Day', cloth: 'forest' },
      { name: 'Summer Fair', cloth: 'umber' },
      { name: '— Class letter', cloth: 'cream' },
      { name: 'Leavers Night', cloth: 'burgundy' },
      { name: 'Trip to France', cloth: 'ink' },
    ],
    closing_title: 'A year group doesn\u2019t end.\nIt becomes something ✦',
    closing_sub:
      'Begin the leavers room. The year group writes the term itself \u2014 through the wall, across the projector, and long after the last day.',
    closing_primary: 'Begin the leavers room ✦',
  },
  prom: {
    label: 'Prom Experience',
    nav_back: 'EventDrop ✦',
    hero_eyebrow: 'For prom leads ✦',
    hero_title: 'The night before the rest of it ✦',
    hero_sub:
      'The dressing-up, the speeches, the dancefloor \u2014 the year group crossing the threshold together while the room is still loud.',
    hero_primary: 'Open the prom room ✦',
    react_title: 'The room reacts together ✦',
    react_body:
      'The year group scans the same QR. The wall lights up as it happens. Speeches, sparklers, last dances \u2014 the year group writing the night, together.',
    memories_eyebrow: 'CHAPTER TWO ✦',
    memories_title: 'The night the year group crossed ✦',
    memories_sub:
      'A final chapter the year group writes together \u2014 not a phone roll, not a Drive folder. The threshold, marked.',
    memories_cards: [
      { pen: '#D4AF5E', caption: 'Speech of the year ✦' },
      { pen: '#C84A4A', caption: 'Three years in one night ♡' },
      { pen: '#5B7B8C', caption: 'I will not forget this' },
    ],
    archive_eyebrow: 'CHAPTER THREE ✦',
    archive_title: 'Proof of the night the year group crossed ✦',
    archive_sub:
      'The speeches. The late hour. The first dance. Each chapter \u2014 proof the year group was there, on that night, together.',
    archive_spines: [
      { name: 'First Dances', cloth: 'ink' },
      { name: 'The Speeches', cloth: 'forest' },
      { name: '— Letter to the room', cloth: 'cream' },
      { name: 'Late Night', cloth: 'burgundy' },
      { name: 'After Hours', cloth: 'umber' },
    ],
    closing_title: 'Some proms end.\nThis one becomes the year group\u2019s threshold ✦',
    closing_sub:
      'Begin the prom room. The year group writes the night together \u2014 through the room, across the projector, and into the years ahead.',
    closing_primary: 'Begin the prom room ✦',
  },
  hub: {
    label: 'For schools',
    nav_back: 'EventDrop ✦',
    hero_eyebrow: 'FOR SCHOOLS ✦',
    hero_title: 'Three rooms where the year group writes itself ✦',
    hero_sub:
      'Assembly films. Year 6 leavers. Year 11 prom. Each one a room the year group enters together \u2014 written while the term is still happening, returned to years later.',
    hero_primary: null, // hub has no single primary CTA
    closing_title: 'Three rooms.\nThe year group writes each one ✦',
    closing_sub:
      'Choose the one that matches the term \u2014 the assembly, the leavers room, or the prom. Each one is a room the class enters together.',
    closing_primary: null,
  },
};

// ── shared primitives ─────────────────────────────────────────────
const Eyebrow = ({ children }) => (
  <div style={{
    fontFamily: SERIF, fontStyle: 'italic',
    fontSize: 11, letterSpacing: '0.28em', textTransform: 'uppercase',
    color: GOLD_MUTED, marginBottom: 14,
  }}>{children}</div>
);

const Heading = ({ children, max = 760, size = 'clamp(32px, 5.2vw, 60px)' }) => (
  <h2 style={{
    margin: 0,
    fontFamily: SERIF,
    fontSize: size,
    fontWeight: 500, lineHeight: 1.1,
    letterSpacing: '-0.015em',
    color: IVORY,
    textShadow: '0 0 28px rgba(212,170,82,0.18)',
    maxWidth: max,
  }}>{children}</h2>
);

const SubCopy = ({ children, max = 580 }) => (
  <p style={{
    margin: '20px 0 0',
    maxWidth: max,
    fontFamily: SERIF, fontStyle: 'italic',
    fontSize: 'clamp(15px, 1.4vw, 18px)',
    lineHeight: 1.62,
    color: INK_DIM,
  }}>{children}</p>
);

const Hairline = ({ width = 40, opacity = 0.32 }) => (
  <div aria-hidden style={{
    width, height: 1,
    background: `rgba(212,170,82,${opacity})`,
    margin: '12px 0 16px',
  }} />
);

export const GhostPill = ({ to, onClick, children, testId, variant = 'primary' }) => {
  const C = to ? Link : 'button';
  const props = to ? { to } : { type: 'button' };
  const [pressed, setPressed] = useState(false);
  const press = () => setPressed(true);
  const release = () => setPressed(false);
  const base = {
    primary: {
      height: 54, padding: '0 28px',
      bg: 'rgba(18,12,6,0.65)',
      border: 'rgba(212,170,82,0.45)',
      pressedBorder: 'rgba(212,170,82,0.20)',
      color: '#FFE7A8',
    },
    quiet: {
      height: 44, padding: '0 18px',
      bg: 'transparent',
      border: 'rgba(212,170,82,0.26)',
      pressedBorder: 'rgba(212,170,82,0.14)',
      color: 'rgba(255,235,200,0.78)',
    },
  }[variant];
  return (
    <C
      {...props}
      data-testid={testId}
      data-pressed={pressed ? '1' : '0'}
      onClick={onClick}
      onPointerDown={press}
      onPointerUp={release}
      onPointerLeave={release}
      onPointerCancel={release}
      style={{
        display: 'inline-flex', alignItems: 'center', gap: 8,
        height: base.height, padding: base.padding, borderRadius: 999,
        background: base.bg,
        border: `1px solid ${pressed ? base.pressedBorder : base.border}`,
        backdropFilter: 'blur(12px)',
        color: base.color,
        fontFamily: SERIF, fontStyle: 'italic',
        fontSize: variant === 'primary' ? 16 : 14, letterSpacing: '0.01em',
        textDecoration: 'none', cursor: 'pointer',
        boxShadow: '0 10px 28px rgba(0,0,0,0.40)',
        transform: pressed ? 'translateY(1px)' : 'translateY(0)',
        transition: 'transform 220ms ease, border-color 220ms ease',
      }}
    >
      {children}
    </C>
  );
};

const QuietLink = ({ to, onClick, children, testId }) => (
  <Link
    to={to || '#'}
    onClick={onClick}
    data-testid={testId}
    style={{
      fontFamily: SERIF, fontStyle: 'italic',
      fontSize: 14, color: 'rgba(255,235,200,0.68)',
      textDecoration: 'underline',
      textDecorationColor: 'rgba(212,170,82,0.28)',
      textUnderlineOffset: 4,
    }}
  >
    {children}
  </Link>
);

// ── decorative atoms ──────────────────────────────────────────────
const DustMote = ({ top, delay = 0 }) => (
  <div aria-hidden style={{
    position: 'absolute', top, left: '8%',
    width: 3, height: 3, borderRadius: 999,
    background: 'rgba(255,224,176,0.50)',
    filter: 'blur(0.6px)',
    animation: `schoolsChapterDust 24s linear ${delay}s infinite`,
    pointerEvents: 'none',
  }} />
);

// ── 1 · NAV — softens like the homepage ────────────────────────────
export const SchoolsNav = ({ audience }) => {
  const copy = AUDIENCE[audience];
  return (
    <header style={{
      position: 'sticky', top: 0, zIndex: 30,
      padding: '20px 28px',
      background: 'rgba(14,8,5,0.6)',
      backdropFilter: 'blur(14px)',
      borderBottom: '1px solid rgba(212,170,82,0.10)',
    }}>
      <div style={{
        maxWidth: 1200, margin: '0 auto',
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        gap: 18,
      }}>
        <Link to="/" style={{
          fontFamily: SERIF, fontSize: 22, color: IVORY,
          textDecoration: 'none', letterSpacing: '-0.005em',
        }}>
          {copy.nav_back}
        </Link>
        <nav style={{ display: 'flex', alignItems: 'center', gap: 22 }}>
          <Link to="/schools" style={{
            fontFamily: SERIF, fontStyle: 'italic',
            fontSize: 14, color: 'rgba(255,235,200,0.72)',
            textDecoration: 'underline',
            textDecorationColor: 'rgba(212,170,82,0.22)',
            textUnderlineOffset: 4,
          }}>
            For schools
          </Link>
          <Link to="/login" style={{
            fontFamily: SERIF, fontStyle: 'italic',
            fontSize: 14, color: 'rgba(255,235,200,0.72)',
            textDecoration: 'underline',
            textDecorationColor: 'rgba(212,170,82,0.22)',
            textUnderlineOffset: 4,
          }}>
            Sign in
          </Link>
        </nav>
      </div>
    </header>
  );
};

// ── 2 · HERO — "The night arrives ✦" ───────────────────────────────
export const SchoolsHero = ({ audience, onPrimary }) => {
  const copy = AUDIENCE[audience];
  return (
    <section
      data-testid="schools-hero"
      style={{
        position: 'relative',
        background: PAPER_BG,
        padding: 'clamp(80px, 12vw, 160px) clamp(24px, 6vw, 80px) clamp(60px, 9vw, 120px)',
        overflow: 'hidden',
      }}
    >
      {/* subtle radial warmth + dust motes */}
      <div aria-hidden style={{
        position: 'absolute', inset: 0,
        background:
          'radial-gradient(ellipse at 20% 30%, rgba(212,170,82,0.10) 0%, transparent 50%), ' +
          'radial-gradient(ellipse at 80% 70%, rgba(140,80,40,0.08) 0%, transparent 55%)',
        pointerEvents: 'none',
      }} />
      <DustMote top="22%" delay={0} />
      <DustMote top="48%" delay={6.5} />
      <DustMote top="70%" delay={13} />

      <div style={{
        maxWidth: 1100, margin: '0 auto',
        display: 'grid',
        gridTemplateColumns: 'minmax(0, 1.05fr) minmax(0, 0.95fr)',
        gap: 'clamp(40px, 6vw, 80px)',
        alignItems: 'center',
        position: 'relative',
        zIndex: 1,
      }}>
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, ease: [0.4, 0, 0.2, 1] }}
        >
          <Eyebrow>{copy.hero_eyebrow}</Eyebrow>
          <Heading>{copy.hero_title}</Heading>
          <SubCopy max={520}>{copy.hero_sub}</SubCopy>
          {copy.hero_primary && (
            <div style={{ marginTop: 36, display: 'flex', flexWrap: 'wrap', gap: 18, alignItems: 'center' }}>
              <GhostPill testId="schools-hero-cta" onClick={onPrimary}>
                {copy.hero_primary}
              </GhostPill>
            </div>
          )}
        </motion.div>

        {/* Right column — yearbook opening at night, NOT a feature card */}
        <YearbookHeroVignette audience={audience} />
      </div>

      {/* responsive collapse */}
      <style>{`
        @keyframes schoolsChapterDust {
          0%   { transform: translate(0, 0) scale(1); opacity: 0.0; }
          12%  { opacity: 0.65; }
          50%  { transform: translate(40vw, -18px) scale(1.05); opacity: 0.5; }
          88%  { opacity: 0.0; }
          100% { transform: translate(86vw, -10px) scale(1); opacity: 0.0; }
        }
        @media (max-width: 980px) {
          [data-testid="schools-hero"] > div:last-of-type {
            display: block !important;
          }
          [data-testid="schools-hero"] > div:last-of-type > * + * {
            margin-top: 56px;
          }
        }
        @media (prefers-reduced-motion: reduce) {
          [data-testid="schools-hero"] *, [data-testid^="schools-"] * {
            animation: none !important;
          }
        }
      `}</style>
    </section>
  );
};

// ── 3 · YEARBOOK HERO VIGNETTE (right column of hero) ──────────────
const YearbookHeroVignette = ({ audience }) => {
  const cards = AUDIENCE[audience].memories_cards ||
    AUDIENCE.year6.memories_cards;
  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.97 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 1.2, delay: 0.2, ease: [0.4, 0, 0.2, 1] }}
      style={{
        position: 'relative',
        aspectRatio: '4 / 5',
        background:
          'linear-gradient(160deg, #1A1108 0%, #0F0805 100%)',
        border: '1px solid rgba(212,170,82,0.16)',
        borderRadius: 4,
        boxShadow:
          '0 30px 80px rgba(0,0,0,0.55), 0 0 0 1px rgba(212,170,82,0.08) inset',
        overflow: 'hidden',
        padding: 24,
      }}
    >
      {/* parchment inner — like the yearbook page just opened */}
      <div style={{
        position: 'absolute', inset: 14,
        background:
          'linear-gradient(180deg, rgba(248,238,214,0.92) 0%, rgba(238,222,190,0.92) 100%)',
        borderRadius: 2,
        boxShadow:
          '0 12px 32px rgba(0,0,0,0.45), 0 0 0 1px rgba(168,133,76,0.18)',
      }} />

      {/* projector glow from above */}
      <div aria-hidden style={{
        position: 'absolute', top: -100, left: '40%',
        width: 240, height: 200,
        background: 'radial-gradient(ellipse, rgba(255,222,168,0.30) 0%, transparent 60%)',
        filter: 'blur(20px)',
        pointerEvents: 'none',
      }} />

      {/* 3 polaroids — handwritten Caveat captions, slightly rotated */}
      <div style={{
        position: 'absolute', inset: 14,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        gap: 18, padding: 22,
      }}>
        {cards.slice(0, 3).map((c, i) => (
          <Polaroid
            key={i}
            tilt={i === 0 ? -7 : i === 2 ? 8 : -1.5}
            lift={i === 1 ? -8 : i === 0 ? 0 : 4}
            tone={i % 2 === 0 ? '#3a2818' : '#2a1808'}
            caption={c.caption}
            penColor={c.pen}
            delay={i * 0.18}
          />
        ))}
      </div>

      {/* yearbook eyebrow */}
      <div style={{
        position: 'absolute', top: 28, left: '50%', transform: 'translateX(-50%)',
        fontFamily: SERIF, fontStyle: 'italic',
        fontSize: 10, letterSpacing: '0.28em', textTransform: 'uppercase',
        color: 'rgba(168,133,76,0.85)',
        zIndex: 4,
      }}>
        The yearbook ✦ opening at night
      </div>
    </motion.div>
  );
};

// One polaroid card — Pillow-free CSS rendering, soft warm tone.
const Polaroid = ({ tilt, lift, tone, caption, penColor, delay }) => (
  <motion.div
    initial={{ opacity: 0, y: 16, rotate: tilt + 4 }}
    animate={{ opacity: 1, y: lift, rotate: tilt }}
    transition={{ duration: 1.1, delay, ease: [0.4, 0, 0.2, 1] }}
    style={{
      flex: '0 0 auto',
      width: 116,
      height: 160,
      background: '#F7EFD9',
      padding: '8px 8px 28px',
      borderRadius: 1,
      boxShadow: '0 18px 28px rgba(0,0,0,0.55), 0 2px 0 rgba(168,133,76,0.18) inset',
      position: 'relative',
      zIndex: 2,
    }}
  >
    <div style={{
      width: '100%', height: 92,
      background:
        `radial-gradient(ellipse at 30% 30%, rgba(255,210,140,0.35) 0%, transparent 55%), ${tone}`,
      borderRadius: 1,
    }} />
    <div style={{
      marginTop: 8,
      fontFamily: '"Caveat","Snell Roundhand",Georgia,serif',
      fontSize: 14,
      color: penColor,
      textAlign: 'center',
      lineHeight: 1.05,
      transform: 'rotate(-1.5deg)',
    }}>{caption}</div>
  </motion.div>
);

// ── 4 · ROOM REACTS — atmospheric tile ─────────────────────────────
export const SchoolsRoomReacts = ({ audience }) => {
  const copy = AUDIENCE[audience];
  return (
    <section
      data-testid="schools-room-reacts"
      style={{
        position: 'relative',
        background: '#0B0604',
        padding: 'clamp(80px, 10vw, 140px) clamp(24px, 6vw, 80px)',
        overflow: 'hidden',
      }}
    >
      {/* warm continuity band so it reads as physically adjacent */}
      <div aria-hidden style={{
        position: 'absolute', top: 0, left: 0, right: 0, height: 120,
        background: 'linear-gradient(180deg, rgba(14,8,5,0.95) 0%, transparent 100%)',
        pointerEvents: 'none',
      }} />
      <div style={{
        maxWidth: 1100, margin: '0 auto', position: 'relative', zIndex: 1,
        display: 'grid',
        gridTemplateColumns: 'minmax(0, 0.9fr) minmax(0, 1.1fr)',
        gap: 'clamp(40px, 7vw, 90px)',
        alignItems: 'center',
      }}>
        <div>
          <Eyebrow>CHAPTER ONE ✦</Eyebrow>
          <Heading max={460} size="clamp(28px, 4vw, 44px)">{copy.react_title}</Heading>
          <Hairline />
          <SubCopy max={440}>{copy.react_body}</SubCopy>
        </div>
        <ProjectionWall />
      </div>

      <style>{`
        @media (max-width: 880px) {
          [data-testid="schools-room-reacts"] > div {
            grid-template-columns: 1fr !important;
          }
        }
      `}</style>
    </section>
  );
};

const ProjectionWall = () => (
  <div style={{
    position: 'relative',
    aspectRatio: '5 / 4',
    background:
      'linear-gradient(180deg, #1A0F08 0%, #0A0604 100%)',
    border: '1px solid rgba(212,170,82,0.16)',
    borderRadius: 4,
    overflow: 'hidden',
    boxShadow:
      '0 30px 80px rgba(0,0,0,0.6), 0 0 0 1px rgba(212,170,82,0.06) inset',
  }}>
    {/* projected wall — central crossfading panel */}
    <div aria-hidden style={{
      position: 'absolute', top: '14%', left: '18%', right: '18%', bottom: '32%',
      background:
        'radial-gradient(ellipse at 50% 50%, rgba(255,222,168,0.28) 0%, transparent 65%)',
      borderRadius: 2,
      animation: 'schoolsWallPulse 5.4s ease-in-out infinite',
    }} />
    {/* small fairy bulbs across the top edge */}
    <div aria-hidden style={{
      position: 'absolute', top: 22, left: 0, right: 0,
      display: 'flex', justifyContent: 'space-around', padding: '0 10%',
    }}>
      {[0, 1, 2, 3, 4, 5].map((i) => (
        <div key={i} style={{
          width: 6, height: 6, borderRadius: 999,
          background: 'rgba(255,222,168,0.85)',
          boxShadow: '0 0 10px 4px rgba(255,206,144,0.5)',
          animation: `schoolsBulbFlicker ${3 + (i * 0.7) % 3}s ease-in-out ${i * 0.5}s infinite`,
        }} />
      ))}
    </div>
    {/* silhouettes at the bottom — the room watching */}
    <svg
      viewBox="0 0 500 120" preserveAspectRatio="none"
      style={{
        position: 'absolute', bottom: 0, left: 0, right: 0,
        width: '100%', height: '32%',
        opacity: 0.66,
      }}
    >
      <path d="M0,120 L0,82 Q14,72 24,82 L36,82 Q44,68 56,82 L70,82 Q82,72 96,82 L114,82 Q128,68 144,82 L160,82 Q174,72 186,82 L208,82 Q220,68 234,82 L250,82 Q262,72 276,82 L296,82 Q308,68 322,82 L340,82 Q354,72 366,82 L386,82 Q398,68 412,82 L432,82 Q444,72 460,82 L478,82 Q488,72 500,82 L500,120 Z" fill="#050302" />
    </svg>
    <div style={{
      position: 'absolute', top: 14, left: 14,
      fontFamily: SERIF, fontStyle: 'italic',
      fontSize: 9.5, letterSpacing: '0.24em', textTransform: 'uppercase',
      color: 'rgba(212,170,82,0.78)',
    }}>
      THE WALL ✦ ALIVE
    </div>
    <style>{`
      @keyframes schoolsWallPulse {
        0%, 100% { opacity: 0.66; transform: scale(1); }
        50%      { opacity: 0.92; transform: scale(1.02); }
      }
      @keyframes schoolsBulbFlicker {
        0%, 100% { opacity: 0.78; }
        50%      { opacity: 0.96; }
      }
    `}</style>
  </div>
);

// ── 5 · MEMORIES REMAIN — yearbook spread ──────────────────────────
export const SchoolsMemoriesRemain = ({ audience }) => {
  const copy = AUDIENCE[audience];
  return (
    <section
      data-testid="schools-memories-remain"
      style={{
        position: 'relative',
        background: PAPER_BG,
        padding: 'clamp(80px, 10vw, 140px) clamp(24px, 6vw, 80px)',
        overflow: 'hidden',
      }}
    >
      <div style={{
        maxWidth: 1100, margin: '0 auto', position: 'relative', zIndex: 1,
      }}>
        <Eyebrow>{copy.memories_eyebrow}</Eyebrow>
        <Heading max={760} size="clamp(28px, 4vw, 44px)">{copy.memories_title}</Heading>
        <Hairline />
        <SubCopy max={580}>{copy.memories_sub}</SubCopy>

        {/* Yearbook spread — three polaroids on a parchment surface */}
        <div style={{
          marginTop: 56,
          background:
            'linear-gradient(180deg, rgba(248,238,214,0.95) 0%, rgba(240,224,192,0.95) 100%)',
          padding: 'clamp(40px, 6vw, 80px) clamp(28px, 5vw, 60px)',
          borderRadius: 4,
          border: '1px solid rgba(168,133,76,0.22)',
          boxShadow:
            '0 30px 80px rgba(0,0,0,0.45), 0 0 0 1px rgba(168,133,76,0.12) inset',
          display: 'flex', flexWrap: 'wrap',
          justifyContent: 'center', alignItems: 'center',
          gap: 32,
        }}>
          {copy.memories_cards.map((c, i) => (
            <YearbookPolaroid
              key={i}
              tilt={i === 0 ? -5 : i === 1 ? 2 : -3}
              lift={i === 1 ? -10 : i === 0 ? 0 : 6}
              caption={c.caption}
              penColor={c.pen}
              delay={i * 0.15}
              tone={i === 0 ? '#3a2412' : i === 1 ? '#2c1c0e' : '#352010'}
            />
          ))}
        </div>
        <div style={{
          marginTop: 22, textAlign: 'right',
          fontFamily: SERIF, fontStyle: 'italic',
          fontSize: 12, letterSpacing: '0.22em', textTransform: 'uppercase',
          color: GOLD_MUTED,
        }}>
          A YEAR-GROUP SPREAD ✦
        </div>
      </div>
    </section>
  );
};

const YearbookPolaroid = ({ tilt, lift, caption, penColor, delay, tone }) => (
  <motion.div
    initial={{ opacity: 0, y: 16, rotate: tilt + 5 }}
    whileInView={{ opacity: 1, y: lift, rotate: tilt }}
    viewport={{ once: true, amount: 0.4 }}
    transition={{ duration: 1.2, delay, ease: [0.4, 0, 0.2, 1] }}
    style={{
      width: 200,
      background: '#FCF5E0',
      padding: '14px 14px 44px',
      borderRadius: 2,
      boxShadow:
        '0 18px 36px rgba(60,38,18,0.42), 0 2px 0 rgba(168,133,76,0.18) inset',
    }}
  >
    <div style={{
      width: '100%', height: 168,
      background:
        `radial-gradient(ellipse at 40% 30%, rgba(255,210,140,0.40) 0%, transparent 55%), ${tone}`,
      borderRadius: 1,
    }} />
    <div style={{
      marginTop: 16,
      fontFamily: '"Caveat","Snell Roundhand",Georgia,serif',
      fontSize: 22,
      color: penColor,
      textAlign: 'center',
      lineHeight: 1.0,
      transform: 'rotate(-1.5deg)',
    }}>{caption}</div>
  </motion.div>
);

// ── 6 · ARCHIVE WAITS — bookshelf vignette ─────────────────────────
const CLOTHS = {
  midnight: 'linear-gradient(180deg, #1F2540 0%, #131830 100%)',
  burgundy: 'linear-gradient(180deg, #5A1F22 0%, #3D1216 100%)',
  forest:   'linear-gradient(180deg, #1E3A2A 0%, #14281C 100%)',
  ink:      'linear-gradient(180deg, #1A2030 0%, #0F141E 100%)',
  umber:    'linear-gradient(180deg, #4A301A 0%, #321F10 100%)',
  cream:    'linear-gradient(180deg, #E8DEC2 0%, #D6C8A4 100%)',
};

export const SchoolsArchiveWaits = ({ audience }) => {
  const copy = AUDIENCE[audience];
  return (
    <section
      data-testid="schools-archive-waits"
      style={{
        position: 'relative',
        background: '#0B0604',
        padding: 'clamp(80px, 10vw, 140px) clamp(24px, 6vw, 80px)',
        overflow: 'hidden',
      }}
    >
      <div style={{
        maxWidth: 1100, margin: '0 auto', position: 'relative', zIndex: 1,
      }}>
        <Eyebrow>{copy.archive_eyebrow}</Eyebrow>
        <Heading max={760} size="clamp(28px, 4vw, 44px)">{copy.archive_title}</Heading>
        <Hairline />
        <SubCopy max={620}>{copy.archive_sub}</SubCopy>

        <ArchiveShelfVignette spines={copy.archive_spines} />

        <div style={{
          marginTop: 22, textAlign: 'right',
          fontFamily: SERIF, fontStyle: 'italic',
          fontSize: 12, letterSpacing: '0.22em', textTransform: 'uppercase',
          color: GOLD_MUTED,
        }}>
          THE SHELF ✦ WAITS
        </div>
      </div>
    </section>
  );
};

const ArchiveShelfVignette = ({ spines }) => (
  <div style={{
    marginTop: 56,
    position: 'relative',
    background:
      'linear-gradient(180deg, #1F1208 0%, #0E0804 100%)',
    padding: '60px 40px 0',
    borderRadius: 4,
    border: '1px solid rgba(212,170,82,0.14)',
    boxShadow:
      '0 30px 80px rgba(0,0,0,0.55), 0 0 0 1px rgba(212,170,82,0.06) inset',
    overflow: 'hidden',
  }}>
    {/* ambient shelf warmth */}
    <div aria-hidden style={{
      position: 'absolute', top: 0, left: 0, right: 0, height: 80,
      background: 'radial-gradient(ellipse at 50% 0%, rgba(255,206,144,0.16) 0%, transparent 60%)',
      pointerEvents: 'none',
    }} />
    {/* the row of spines */}
    <div style={{
      position: 'relative',
      display: 'flex', alignItems: 'flex-end', justifyContent: 'center',
      gap: 6, minHeight: 220,
    }}>
      {spines.map((s, i) => (
        <Spine
          key={i}
          name={s.name}
          cloth={s.cloth}
          delay={i * 0.18}
          tilt={((i * 7) % 5 - 2) * 0.4}
          height={170 + ((i * 11) % 38)}
          thickness={s.name.startsWith('—') ? 18 : 28 + ((i * 5) % 12)}
        />
      ))}
    </div>
    {/* shelf surface */}
    <div aria-hidden style={{
      position: 'relative',
      marginTop: -2,
      height: 14,
      background:
        'linear-gradient(180deg, #3A2412 0%, #1F1208 100%)',
      boxShadow: '0 6px 18px rgba(0,0,0,0.6)',
    }} />
    {/* shelf lip — front edge */}
    <div aria-hidden style={{
      height: 6,
      background: 'linear-gradient(180deg, #4A2D17 0%, #2A1808 100%)',
    }} />
  </div>
);

const Spine = ({ name, cloth, delay, tilt, height, thickness }) => {
  const isLetter = name.startsWith('—');
  const isCream = cloth === 'cream' || isLetter;
  const titleColor = isCream ? '#3a2412' : '#E2C98E';
  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.3 }}
      transition={{ duration: 1.0, delay, ease: [0.4, 0, 0.2, 1] }}
      style={{
        position: 'relative',
        width: thickness, height,
        background: CLOTHS[cloth] || CLOTHS.umber,
        transform: `rotate(${tilt}deg)`,
        transformOrigin: 'bottom center',
        borderRadius: '2px 2px 0 0',
        boxShadow:
          '0 2px 0 rgba(0,0,0,0.4) inset, 0 -2px 0 rgba(255,255,255,0.04) inset, 0 12px 20px rgba(0,0,0,0.5)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        overflow: 'hidden',
      }}
    >
      {/* foil bands */}
      {!isLetter && (
        <>
          <div aria-hidden style={{
            position: 'absolute', top: 14, left: 4, right: 4, height: 1,
            background: 'rgba(212,170,82,0.55)',
          }} />
          <div aria-hidden style={{
            position: 'absolute', bottom: 14, left: 4, right: 4, height: 1,
            background: 'rgba(212,170,82,0.55)',
          }} />
        </>
      )}
      {/* sideways title */}
      <div style={{
        writingMode: 'vertical-rl',
        transform: 'rotate(180deg)',
        fontFamily: SERIF, fontStyle: 'italic',
        fontSize: 9.5, letterSpacing: '0.16em', textTransform: 'uppercase',
        color: titleColor,
        textShadow: isCream ? 'none' : '0 0 6px rgba(212,170,82,0.18)',
        padding: '20px 0',
      }}>
        {name.replace(/^—\s*/, '')}
      </div>
      {/* ribbon for letter */}
      {isLetter && (
        <div aria-hidden style={{
          position: 'absolute', top: 0, bottom: 0, left: '50%',
          transform: 'translateX(-50%)',
          width: 5,
          background: 'linear-gradient(180deg, #D4AF5E 0%, #A8854C 100%)',
          boxShadow: '0 0 4px rgba(212,170,82,0.4)',
        }} />
      )}
    </motion.div>
  );
};

// ── 7 · CLOSING CTA ────────────────────────────────────────────────
export const SchoolsClosing = ({ audience, onPrimary }) => {
  const copy = AUDIENCE[audience];
  const lines = (copy.closing_title || '').split('\n');
  return (
    <section
      data-testid="schools-closing"
      style={{
        position: 'relative',
        background: PAPER_BG,
        padding: 'clamp(80px, 12vw, 160px) clamp(24px, 6vw, 80px)',
        overflow: 'hidden',
        textAlign: 'center',
      }}
    >
      <DustMote top="30%" delay={2} />
      <DustMote top="70%" delay={10} />
      <div style={{ maxWidth: 720, margin: '0 auto', position: 'relative', zIndex: 1 }}>
        <Eyebrow>CLOSING ✦</Eyebrow>
        <h2 style={{
          margin: 0,
          fontFamily: SERIF,
          fontSize: 'clamp(30px, 4.8vw, 52px)',
          fontWeight: 500, lineHeight: 1.14,
          letterSpacing: '-0.015em',
          color: IVORY,
          textShadow: '0 0 32px rgba(212,170,82,0.20)',
        }}>
          {lines.map((l, i) => (
            <React.Fragment key={i}>
              {l}{i < lines.length - 1 && <br />}
            </React.Fragment>
          ))}
        </h2>
        <p style={{
          margin: '22px auto 0', maxWidth: 580,
          fontFamily: SERIF, fontStyle: 'italic',
          fontSize: 'clamp(15px, 1.4vw, 18px)',
          lineHeight: 1.65,
          color: INK_DIM,
        }}>{copy.closing_sub}</p>
        {copy.closing_primary && (
          <div style={{ marginTop: 42, display: 'flex', justifyContent: 'center', gap: 22, flexWrap: 'wrap' }}>
            <GhostPill testId="schools-closing-cta" onClick={onPrimary}>
              {copy.closing_primary}
            </GhostPill>
          </div>
        )}
        {/* Quiet "preserved night" affordance — discovered, never showcased.
            Sits in the closing area as a footer-line, NOT a CTA. Renders
            only when the backend has an editorially-curated demo for the
            audience. */}
        <PreservedDemoLink audience={audience} />
      </div>
    </section>
  );
};

// ── 7b · PreservedDemoLink — tiny italic-serif footer affordance ───
// Reads like a sentence the visitor finishes by accident: italic
// serif, warm ivory at 70% opacity, underline only on hover, NO
// button chrome, NO icon other than an optional tiny ✦. NEVER reads
// "View demo" / "See example" / "Try sample wall" — that vocabulary
// is anti-brand.
const DEMO_COPY = {
  year6: 'A real wall from a recent leavers night ✦',
  prom:  'Some nights are still forming ✦',
  hub:   'Or wander through a room a year group already wrote ✦',
};

const PreservedDemoLink = ({ audience }) => {
  const [featured, setFeatured] = React.useState(null);
  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const base = process.env.REACT_APP_BACKEND_URL;
        const res = await fetch(`${base}/api/demo/featured?audience=${encodeURIComponent(audience)}`);
        if (!res.ok) return;
        const data = await res.json();
        if (!cancelled && data?.featured?.slug) setFeatured(data.featured);
      } catch {
        // Silent — the affordance simply does not surface.
      }
    })();
    return () => { cancelled = true; };
  }, [audience]);

  if (!featured) return null;
  const copy = DEMO_COPY[audience] || DEMO_COPY.hub;
  return (
    <div style={{ marginTop: 30 }} data-testid="schools-preserved-demo">
      <Link
        to={`/?demo=${encodeURIComponent(featured.slug)}`}
        data-testid="schools-preserved-demo-link"
        style={{
          fontFamily: SERIF, fontStyle: 'italic',
          fontSize: 13.5, color: 'rgba(255,235,200,0.70)',
          textDecoration: 'none', letterSpacing: '0.01em',
          transition: 'color 280ms ease, text-decoration-color 280ms ease',
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.color = 'rgba(255,235,200,0.92)';
          e.currentTarget.style.textDecoration = 'underline';
          e.currentTarget.style.textDecorationColor = 'rgba(212,170,82,0.42)';
          e.currentTarget.style.textUnderlineOffset = '4px';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.color = 'rgba(255,235,200,0.70)';
          e.currentTarget.style.textDecoration = 'none';
        }}
      >
        {copy}
      </Link>
    </div>
  );
};

// ── 8 · FOOTER ─────────────────────────────────────────────────────
export const SchoolsFooter = () => (
  <footer style={{
    background: '#0A0503',
    padding: '52px 28px',
    borderTop: '1px solid rgba(212,170,82,0.10)',
    textAlign: 'center',
  }}>
    <div style={{
      fontFamily: SERIF, fontSize: 20, color: IVORY,
      letterSpacing: '-0.005em',
    }}>
      EventDrop ✦
    </div>
    <div style={{
      marginTop: 10,
      fontFamily: SERIF, fontStyle: 'italic',
      fontSize: 12, color: 'rgba(255,235,200,0.46)',
    }}>
      Made with care · for the nights worth keeping ·
      {' '}<Link to="/privacy" style={{ color: GOLD_MUTED, textDecoration: 'underline', textDecorationColor: 'rgba(212,170,82,0.28)' }}>privacy</Link>
      {' · '}<Link to="/terms" style={{ color: GOLD_MUTED, textDecoration: 'underline', textDecorationColor: 'rgba(212,170,82,0.28)' }}>terms</Link>
      {' · © '}{new Date().getFullYear()}
    </div>
  </footer>
);

// ── 9 · HUB-ONLY: three archival plates ────────────────────────────
// The /schools hub presents three preserved-night editions as
// editorial plates — NEVER as pricing-grid feature cards.
export const SchoolsHubPlates = () => {
  const EDITIONS = [
    {
      to: '/classroom',
      eyebrow: 'For class teachers ✦',
      title: 'The Assembly Film',
      sub: 'Turn this week\u2019s photos into a film by Monday morning — written by the class, ready for the hall.',
      cta: 'Open the class film',
      testId: 'schools-edition-classroom',
      lift: 0,
    },
    {
      to: '/year6',
      eyebrow: 'For Year 6 leavers ✦',
      title: 'The Leavers Room',
      sub: 'A yearbook the year group writes together as the term is still happening — a chapter the school can still point to years later.',
      cta: 'Open the leavers room',
      testId: 'schools-edition-year6',
      lift: -18,
      featured: true,
    },
    {
      to: '/prom',
      eyebrow: 'For prom leads ✦',
      title: 'The Prom Room',
      sub: 'The dressing-up, the speeches, the dancefloor — the year group crossing the threshold together while the room is still loud.',
      cta: 'Open the prom room',
      testId: 'schools-edition-prom',
      lift: -8,
    },
  ];
  return (
    <section
      data-testid="schools-hub-plates"
      style={{
        position: 'relative',
        background: '#0B0604',
        padding: 'clamp(60px, 9vw, 120px) clamp(24px, 6vw, 80px) clamp(80px, 10vw, 140px)',
      }}
    >
      <div style={{
        maxWidth: 1180, margin: '0 auto',
        display: 'grid',
        gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
        gap: 'clamp(20px, 3vw, 36px)',
        alignItems: 'end',
      }}>
        {EDITIONS.map((e, i) => (
          <ArchivePlate key={e.to} edition={e} delay={i * 0.18} />
        ))}
      </div>
      <style>{`
        @media (max-width: 900px) {
          [data-testid="schools-hub-plates"] > div {
            grid-template-columns: 1fr !important;
          }
          [data-testid="schools-hub-plates"] > div > * {
            transform: none !important;
          }
        }
      `}</style>
    </section>
  );
};

const ArchivePlate = ({ edition, delay }) => (
  <motion.div
    initial={{ opacity: 0, y: 18 }}
    whileInView={{ opacity: 1, y: edition.lift }}
    viewport={{ once: true, amount: 0.3 }}
    transition={{ duration: 1.0, delay, ease: [0.4, 0, 0.2, 1] }}
    style={{
      position: 'relative',
      background:
        edition.featured
          ? 'linear-gradient(180deg, #1A1008 0%, #0F0804 100%)'
          : 'linear-gradient(180deg, #150D07 0%, #0B0604 100%)',
      border: edition.featured
        ? '1px solid rgba(212,170,82,0.30)'
        : '1px solid rgba(212,170,82,0.16)',
      borderRadius: 4,
      padding: 'clamp(28px, 3vw, 38px)',
      overflow: 'hidden',
      boxShadow:
        edition.featured
          ? '0 30px 80px rgba(0,0,0,0.55), 0 0 0 1px rgba(212,170,82,0.10) inset'
          : '0 20px 60px rgba(0,0,0,0.45)',
    }}
  >
    {edition.featured && (
      <div style={{
        position: 'absolute', top: 22, right: 22,
        fontFamily: SERIF, fontStyle: 'italic',
        fontSize: 10, letterSpacing: '0.24em', textTransform: 'uppercase',
        color: GOLD,
      }}>
        Most chosen ✦
      </div>
    )}
    <div style={{
      fontFamily: SERIF, fontStyle: 'italic',
      fontSize: 11, letterSpacing: '0.26em', textTransform: 'uppercase',
      color: GOLD_MUTED, marginBottom: 14,
    }}>
      {edition.eyebrow}
    </div>
    <div style={{
      fontFamily: SERIF,
      fontSize: 'clamp(22px, 2.4vw, 28px)',
      lineHeight: 1.15, fontWeight: 500,
      color: IVORY,
      letterSpacing: '-0.01em',
      marginBottom: 18,
    }}>
      {edition.title}
    </div>
    <div aria-hidden style={{
      width: 36, height: 1,
      background: 'rgba(212,170,82,0.32)',
      marginBottom: 18,
    }} />
    <p style={{
      margin: 0,
      fontFamily: SERIF, fontStyle: 'italic',
      fontSize: 15, lineHeight: 1.65,
      color: INK_DIM,
    }}>
      {edition.sub}
    </p>
    <div style={{ marginTop: 28 }}>
      <GhostPill to={edition.to} testId={edition.testId} variant={edition.featured ? 'primary' : 'quiet'}>
        {edition.cta} →
      </GhostPill>
    </div>
  </motion.div>
);
